<template>
    <div :data-test="`base-fee-${testId(props.fee.key)}`">
        <div class="flex">
            <div
                v-if="props.fee.routingField"
                class="w-12 flex items-center justify-center"
                :class="[
                    style.background,
                    { 'rounded-tl-lg': props.first, 'rounded-bl-lg': props.last && !props.editing },
                ]"
            >
                <font-awesome-icon :icon="style.icon" :class="style.color" data-test="base-fee-payer-icon" />
            </div>
            <div class="flex-1 flex justify-between items-center px-6" :class="props.editing ? 'py-3' : 'py-4'">
                <span :id="labelId" class="text-gray-700">{{ props.fee.label }}</span>
                <div v-if="!props.editing" class="flex flex-col items-end">
                    <span v-if="'flat' === props.fee.kind" class="font-mono text-gray-900">
                        {{ formatMoney(Number(props.item[amountField])) }}
                    </span>
                    <span v-else class="font-mono text-gray-900">
                        {{ props.item[quantityField] }} × ${{ Number(props.item[rateField]).toFixed(5) }}
                    </span>
                </div>
                <div v-else class="flex items-start gap-2">
                    <div v-if="'flat' === props.fee.kind" class="flex flex-col items-end">
                        <vx-input
                            :modelValue="String(values[amountField] ?? '')"
                            type="number"
                            :step="0.00001"
                            :min="0"
                            :max="1000000"
                            size="sm"
                            class="w-40! text-right font-mono"
                            placeholder="0.00000"
                            :error="props.errors[amountField]"
                            :data-test="`billing-item-input-${testId(amountField)}`"
                            @update:modelValue="setValue(amountField, $event)"
                        />
                    </div>
                    <template v-else>
                        <div class="flex flex-col items-end">
                            <vx-input
                                :modelValue="String(values[quantityField] ?? '')"
                                type="number"
                                :min="0"
                                :max="10000"
                                size="sm"
                                class="text-right font-mono w-24!"
                                placeholder="Quantity"
                                :error="props.errors[quantityField]"
                                :data-test="`billing-item-input-${testId(quantityField)}`"
                                @update:modelValue="setValue(quantityField, $event)"
                            />
                        </div>
                        <div class="flex flex-col items-end">
                            <vx-input
                                :modelValue="String(values[rateField] ?? '')"
                                type="number"
                                :step="0.00001"
                                :min="0"
                                :max="1000000"
                                size="sm"
                                class="w-40! text-right font-mono"
                                placeholder="Rate"
                                :error="props.errors[rateField]"
                                :data-test="`billing-item-input-${testId(rateField)}`"
                                @update:modelValue="setValue(rateField, $event)"
                            />
                        </div>
                    </template>
                    <vx-button
                        type="button"
                        size="sm"
                        color="muted"
                        :aria-label="`Remove ${props.fee.label}`"
                        :data-test="`remove-base-fee-${testId(props.fee.key)}`"
                        @click="emit('remove')"
                    >
                        <font-awesome-icon widthAuto :icon="faXmark" />
                    </vx-button>
                </div>
            </div>
        </div>
        <div
            v-if="props.editing && props.fee.routingField && props.routingOptions"
            class="flex flex-col gap-2 px-6 py-3 bg-gray-50"
            :class="{ 'rounded-b-lg': props.last }"
        >
            <div
                role="radiogroup"
                :aria-labelledby="labelId"
                :aria-describedby="routingDescriptionId"
                class="flex gap-4"
                :data-test="testId(routingField)"
            >
                <label v-for="option in props.routingOptions" :key="option.value" class="flex items-center gap-2">
                    <input
                        type="radio"
                        :name="routingName"
                        :value="option.value"
                        :checked="values[routingField] === option.value"
                        class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        :data-test="`${testId(routingField)}-${testId(option.value)}`"
                        @change="setValue(routingField, option.value)"
                    />
                    <span class="text-sm text-gray-700">{{ option.label }}</span>
                </label>
            </div>
            <div :id="routingDescriptionId" class="text-xs text-gray-500">{{ props.fee.routingDescription }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, useId } from 'vue';
import { VxButton, VxInput } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import {
    type BaseFee,
    type BaseFeeRoutingOption,
    type PricingValues,
    formatMoney,
    isBaseFeeInUse,
    NOT_IN_USE_STYLE,
    payerStyle,
    testId,
} from '~/components/kiosk/kiosk-billing/baseFees';

const props = defineProps<{
    fee: BaseFee;
    item: PricingValues;
    editing: boolean;
    errors: Record<string, string>;
    routingOptions?: readonly BaseFeeRoutingOption[];
    first: boolean;
    last: boolean;
}>();

const emit = defineEmits<{ remove: [] }>();

const values = defineModel<Record<string, unknown>>('values', { required: true });

const labelId = useId();
const routingName = useId();
const routingDescriptionId = useId();

const amountField = computed(() => ('flat' === props.fee.kind ? props.fee.amountField : ''));
const quantityField = computed(() => ('quantity' === props.fee.kind ? props.fee.quantityField : ''));
const rateField = computed(() => ('quantity' === props.fee.kind ? props.fee.rateField : ''));
const routingField = computed(() => props.fee.routingField ?? '');

const source = computed<PricingValues>(() => (props.editing ? values.value : props.item));

const style = computed(() =>
    isBaseFeeInUse(props.fee, source.value) ? payerStyle(source.value[routingField.value]) : NOT_IN_USE_STYLE
);

const setValue = (field: string, value: unknown): void => {
    values.value = { ...values.value, [field]: value };
};
</script>
